import React, { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import { Modal, Box, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import AddYearModal from '../components/AddYearModal';
import api from '../axios/api';
import { FilePenLine, Trash2, CalendarCheck } from 'lucide-react';
import dayjs from 'dayjs';
import { showNotification } from '../redux/slices/notificationSlice';
import { useDispatch } from 'react-redux';
import CustomButton from '../componentsII/Buttons';
import { setSelectedYear } from '../redux/slices/authSlice';
import { useSelector } from 'react-redux';

const YearSetting = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [yearsData, setYearData] = useState([]);
  const user = useSelector((state) => state.user.user);
  const [editData, setEditData] = useState(null);
  const dispatch = useDispatch();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [yearConfirmDialogOpen, setYearConfirmDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedYearId, setSelectedYearId] = useState(null);

  const handleOpenModal = (data = null) => {
    setEditData(data);
    setIsModalOpen(true);
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
  };

  const confirmyeardelete = async () => {
    try {
      const response = await api.delete(`/company/deletecompanydatayear/${deleteId}`);
      if (response.data) {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "success",
          })
        );
        setConfirmDialogOpen(false);
        fetchYearData();
      } else {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "error",
          })
        );
        setConfirmDialogOpen(false);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setConfirmDialogOpen(false);
    }
  };

  const handleCloseModal = () => {
    setEditData(null);
    setIsModalOpen(false);
  };

  const handleEditClick = (yearData) => {
    const formattedData = {
      ...yearData,
      is_base_year: yearData.is_base_year === "1",
      is_target_year: yearData.is_target_year === "1",
      target_reduction_percentage: parseFloat(yearData.target_reduction_percentage)
    };
    handleOpenModal(formattedData);
  };

  useEffect(() => {
    fetchYearData();
  }, []);

  const fetchYearData = async () => {
    try {
      const response = await api.get(`/company/companyDataYear?company_id=${user.company.id}`);
      if (response.data.data) {
        setYearData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleSuccess = (success = true, message = '') => {
    if (success) {
      window.location.reload();
      dispatch(
        showNotification({
          message: 'year data saved successfully!',
          type: "success",
        })
      );
      fetchYearData();
    } else {
      dispatch(
        showNotification({
          message: message || 'Error saving year data',
          type: "error",
        })
      );
    }
  };

  const handleSelectedYearChange = (year) => {
    setYearConfirmDialogOpen(true);
    setSelectedYearId(year);
  };

  const handleCancelYearChange = () => {
    setYearConfirmDialogOpen(false);
  };

  const updateLocalStorageUserYear = () => {
    try {
      const storedState = localStorage.getItem('user');
      if (!storedState) return;
  
      const parsedState = JSON.parse(storedState);
  
      if (parsedState && parsedState.selectedYear) {
        parsedState.selectedYear = {
          ...parsedState.selectedYear,
          id: selectedYearId.id,
          startYearMonth: selectedYearId.start_year_month,
          start_year: selectedYearId.start_year,
          endYearMonth: selectedYearId.end_year_month,
          end_year: selectedYearId.end_year,
          isBaseYear: selectedYearId.is_base_year === '1',
          isTargetYear: selectedYearId.is_target_year === '1',
          isSelectedYear: selectedYearId.is_selected_year === 1,
          is_copied: selectedYearId.is_copied,
          targetReductionPercentage: selectedYearId.target_reduction_percentage
        };
  
        localStorage.setItem('user', JSON.stringify(parsedState));
      }
    } catch (error) {
      console.error('Error updating selected year in localStorage:', error);
    }
  };
  

  const handleConfirmYearChange = async () => {
    try {
      const response = await api.put('/company/updateSelectedCompanyDataYear', {
        year_id: selectedYearId.id,
        company_id: user.company.id
      });

      // Assuming a successful response contains a status or data object
      if (response.status === 200) {
        dispatch(setSelectedYear(selectedYearId));
        updateLocalStorageUserYear(); // Update local storage with the new selected year
        showNotification({
          message: 'Year selection updated successfully!',
          type: 'success',
        });

        // setYearConfirmDialogOpen(false);

        setTimeout(() => {
          showNotification({
            message: 'Year selection updated successfully!',
            type: 'success',
          });
          setYearConfirmDialogOpen(false);
          window.location.reload();
        }, 100);

      } else {
        showNotification({
          message: 'Failed to update the year selection. Please try again.',
          type: 'error',
        });
      }
    } catch (error) {
      showNotification({
        message: error.response?.data?.message || 'An error occurred while updating the year.',
        type: 'error',
      });
      console.error('Error updating selected year:', error);
    }
  };


  return (
    <div className="p-6">
      {/* Header Section */}
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-green-600 transition duration-300 ease-in-out hover:text-green-800">
            Data Reporting Year
          </h1>
          <div className="mt-4 space-y-2 text-gray-600">
            <p>Configure your company's fiscal years and set important targets for emissions reduction.</p>
            <p>Define your base year to establish a starting point for measuring progress and set target years with specific reduction goals.</p>
            <p>This data will be used throughout the system to track and visualize your company's environmental impact over time.</p>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <button
            className="bg-green-600 hover:bg-green-700 text-white px-2 py-2 rounded-lg flex items-center gap-2 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            onClick={() => handleOpenModal()}
            title='Add Year'
          >
            <Plus size={20} />
            Add Year
          </button>
        </div>

        <div className='bg-gray-200 h-0.5 m-1'></div>

        {yearsData.length === 0 ? (
          // Empty State
          <div className="text-center py-16 bg-gray-50 rounded-lg border border-gray-200 shadow-sm">
            <img
              src="/vectors/Year settings.jpg"
              alt="No Data"
              className="w-80 h-80 mx-auto text-gray-400 mb-4"
            />
            <h3 className="text-xl font-medium text-gray-600 mb-2">No Year Found</h3>
            <p className="text-gray-500">Add your first year to get started</p>
          </div>
        ) : (
          // Table
          <div className="bg-white rounded-lg shadow-md border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start year</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start month</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End year</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End month</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Base Year</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target Year</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reduction Target</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {yearsData.map((year) => (
                    <tr key={year.id} className={` ${year.is_selected_year === 1 ? 'bg-green-200' : 'hover:bg-gray-50'}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{year.start_year}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{dayjs().month(year.start_year_month).format('MMMM')}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{year.end_year}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{dayjs().month(year.end_year_month).format('MMMM')}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${year.is_base_year === "1" ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {year.is_base_year === "1" ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${year.is_target_year === "1" ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {year.is_target_year === "1" ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {year.target_reduction_percentage > 0 ? (
                          <span className="text-blue-600">{year.target_reduction_percentage}%</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditClick(year)}
                            className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200"
                            aria-label="Edit"
                            title='edit this year'
                          >
                            <FilePenLine size={20} />
                          </button>
                          <button
                            onClick={() => {
                              setConfirmDialogOpen(true);
                              setDeleteId(year.id);
                            }}
                            className={`p-2 text-white rounded-lg transition duration-200 ${year.is_selected_year === 1
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-red-500 hover:bg-red-600'
                              }`}
                            aria-label="Edit"
                            disabled={year.is_selected_year === 1}
                            title='delete this year'
                          >
                            <Trash2 size={20} />
                          </button>
                          {
                            <button
                              onClick={() => handleSelectedYearChange(year)}
                              className={`p-2 text-white rounded-lg transition duration-200 ${year.is_selected_year === 1
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-green-500 hover:bg-green-600'
                                }`}
                              disabled={year.is_selected_year === 1}
                              title='set as reporting year'
                            >
                              <CalendarCheck size={20} />
                            </button>
                          }
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="add-year-modal-title"
        aria-describedby="add-year-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: '8px',
          }}
        >
          <button
            onClick={handleCloseModal}
            className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
            aria-label="Close"
          >
            <X size={24} />
          </button>
          <AddYearModal availableYears={yearsData} editData={editData} onClose={handleCloseModal} onSuccess={handleSuccess} />
        </Box>
      </Modal>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="year-change-dialog-title"
      >
        <DialogTitle id="year-change-dialog-title">
          Confirm Year Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this year? you will lose all the data associated with this year.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <div className="w-full flex justify-end gap-5 items-center">
            <CustomButton
              className="bg-red-500 hover:bg-red-700 text-white "
              click={() => handleCancelDelete()}
              text="Cancel"
            />
            <CustomButton
              className="bg-green-600 hover:bg-green-700 text-white "
              text="Confirm"
              click={confirmyeardelete}
            />
          </div>
        </DialogActions>
      </Dialog>

      <Dialog
        open={yearConfirmDialogOpen}
        onClose={handleCancelYearChange}
        aria-labelledby="year-change-dialog-title"
      >
        <DialogTitle id="year-change-dialog-title">
          Confirm Year Change
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to change the year? This action will change your current selected year.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <div className="w-full flex justify-end gap-5 items-center">
            <CustomButton
              className="bg-red-500 hover:bg-red-700 text-white "
              click={() => handleCancelYearChange()}
              text="Cancel"
            />
            <CustomButton
              className="bg-green-600 hover:bg-green-700 text-white "
              text="Confirm"
              click={handleConfirmYearChange}
            />
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default YearSetting;